import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    BarChart3,
    Building2,
    Coins,
    FileSpreadsheet,
    FileText,
    LayoutDashboard,
    LogOut,
    MapPin,
    Megaphone,
    Menu,
    ShieldCheck,
    Users,
    X,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import style from "../style/components/HeaderV2.module.css";

const navigation = [
    { text: "대시보드", router: "/dashboard", icon: LayoutDashboard },
    { text: "키오스크 관리", router: "/", icon: MapPin },
    { text: "캠페인 관리", router: "/campaigns", icon: Megaphone },
    { text: "회원/적립 현황", router: "/members", icon: Users },
    { text: "계정 관리", router: "/admin", icon: ShieldCheck },
    { text: "파트너사 관리", router: "/partner", icon: Building2 },
    { text: "공지사항 관리", router: "/notice", icon: FileText },
    { text: "엑셀 출력", router: "/excel", icon: FileSpreadsheet },
    { text: "통계", router: "/stats", icon: BarChart3 },
    { text: "환전 요청", router: "/point", icon: Coins, exact: true },
    { text: "환전 방법", router: "/point/withdraw-methods", icon: FileText },
];

const Header = () => {
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const location = useLocation();
    const { userType, signOutUser } = useAuth();

    useEffect(() => {
        document.body.style.overflow = openMenu ? "hidden" : "auto";
    }, [openMenu]);

    const logout = async () => {
        try {
            await signOutUser();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <aside className={style["sidebar"]}>
                <SidebarContent activePath={location.pathname} userType={userType} onLogout={logout} />
            </aside>

            {openMenu && (
                <div className={style["drawer"]} onClick={() => setOpenMenu(false)}>
                    <div className={style["drawer-panel"]} onClick={(event) => event.stopPropagation()}>
                        <div className={style["drawer-close"]}>
                            <button className={style["icon-button"]} onClick={() => setOpenMenu(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <SidebarContent
                            activePath={location.pathname}
                            userType={userType}
                            onNavigate={() => setOpenMenu(false)}
                            onLogout={logout}
                        />
                    </div>
                </div>
            )}

            <header className={style["topbar"]}>
                <div className={style["topbar-left"]}>
                    <button className={[style["icon-button"], style["mobile-menu"]].join(" ")} onClick={() => setOpenMenu(true)}>
                        <Menu size={18} />
                    </button>
                    <span className={style["topbar-date"]}>{formatToday()}</span>
                    <span className={style["topbar-divider"]} />
                    <span className={style["topbar-role"]}>{userType === "SUPER_ADMIN" ? "최고 관리자" : "관리자"}</span>
                </div>

                <div className={style["topbar-actions"]}>
                    <QuickAction to="/dashboard" text="대시보드" />
                    <QuickAction to="/point" text="환전 요청" />
                    <QuickAction to="/excel" text="엑셀 출력" />
                </div>
            </header>
        </>
    );
};

const QuickAction = ({ to, text }: { to: string; text: string }) => (
    <Link className={style["quick-action"]} to={to}>
        {text}
    </Link>
);

const formatToday = () => {
    const date = new Date();
    return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
    });
};

const SidebarContent = ({
    activePath,
    userType,
    onNavigate,
    onLogout,
}: {
    activePath: string;
    userType: string | null;
    onNavigate?: () => void;
    onLogout: () => void;
}) => {
    return (
        <>
            <div className={style["brand"]}>
                <div className={style["brand-mark"]}>I</div>
                <div>
                    <p className={style["brand-title"]}>Admin</p>
                    <p className={style["brand-subtitle"]}>Backoffice</p>
                </div>
            </div>
            <nav className={style["nav"]}>
                {navigation.map((item) => {
                    const active =
                        item.router === "/"
                            ? activePath === "/"
                            : item.exact
                            ? activePath === item.router
                            : activePath === item.router || activePath.startsWith(`${item.router}/`);
                    const Icon = item.icon;

                    return (
                        <Link
                            key={item.router}
                            to={item.router}
                            className={[style["nav-link"], active && style["active"]].filter(Boolean).join(" ")}
                            onClick={onNavigate}
                        >
                            <Icon size={18} />
                            {item.text}
                        </Link>
                    );
                })}
            </nav>
            <div className={style["sidebar-profile"]}>
                <div className={style["profile-row"]}>
                    <div className={style["avatar"]}>관</div>
                    <div className={style["profile-text"]}>
                        <div className={style["profile-name"]}>관리자</div>
                        <div className={style["profile-role"]}>{userType === "SUPER_ADMIN" ? "최고 관리자" : "관리자"}</div>
                    </div>
                </div>
                <button className={style["logout-button"]} onClick={onLogout}>
                    <LogOut size={15} />
                    로그아웃
                </button>
            </div>
        </>
    );
};

export default Header;

/*
const RouterButton = ({ text, router }: { text: string; router: string }) => {
    const [hovered, setHovered] = useState<boolean>(false);
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <Link to={router}>
            <button
                // hover events
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--router"],
                    hovered && style["button--router--hovered"],
                    clicked && style["button--router--clicked"],
                ].join(" ")}
            >
                {text}
            </button>
        </Link>
    );
};
*/
